import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderReport from '../components/OrderReport';
import StockLogsReport from '../components/StockLogsReport';

function Report() {
   const { t } = useTranslation();
   const [selectedReportType, setSelectedReportType] = useState(null);

   return (
      <div className="px-5 relative">
         {/* Report Type Selection Grid */}
         {!selectedReportType && (
            <div className="mt-12">
               <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-gray-800 mb-3">{t('report.choose_your_report')}</h2>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                     {t('report.report_description')}
                  </p>
               </div>
               
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                  {/* Order Report Card */}
                  <div 
                     onClick={() => setSelectedReportType('order')}
                     className="group relative bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 rounded-2xl p-8 cursor-pointer hover:border-blue-400 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-2 overflow-hidden"
                  >
                     {/* Background Pattern */}
                     <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
                     <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-300 opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
                     
                     <div className="relative z-10">
                        <div className="flex items-start mb-6">
                           <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                           </div>
                           <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-blue-700 transition-colors duration-300">{t('report.order_report')}</h3>
                              <p className="text-blue-600 font-medium">{t('report.order_report_subtitle')}</p>
                           </div>
                        </div>
                        
                        <div className="space-y-3 mb-6">
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.order_details_customer_info')}</span>
                           </div>
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.payment_tracking_due_amounts')}</span>
                           </div>
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.date_range_filtering')}</span>
                           </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                           <span className="text-sm text-gray-600">{t('report.click_to_generate_report')}</span>
                           <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Stock Logs Report Card */}
                  <div 
                     onClick={() => setSelectedReportType('stocklogs')}
                     className="group relative bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 rounded-2xl p-8 cursor-pointer hover:border-green-400 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-2 overflow-hidden"
                  >
                     {/* Background Pattern */}
                     <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
                     <div className="absolute bottom-0 left-0 w-24 h-24 bg-green-300 opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
                     
                     <div className="relative z-10">
                        <div className="flex items-start mb-6">
                           <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                           </div>
                           <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-green-700 transition-colors duration-300">{t('report.stock_logs_report')}</h3>
                              <p className="text-green-600 font-medium">{t('report.stock_logs_report_subtitle')}</p>
                           </div>
                        </div>
                        
                        <div className="space-y-3 mb-6">
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.stock_movement_tracking')}</span>
                           </div>
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.inventory_change_history')}</span>
                           </div>
                           <div className="flex items-center text-gray-700">
                              <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm">{t('report.update_tracking_by_user')}</span>
                           </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                           <span className="text-sm text-gray-600">{t('report.click_to_generate_report')}</span>
                           <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center group-hover:bg-green-600 transition-colors duration-300">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
               
               {/* Additional Info Section */}
               <div className="mt-16 text-center">
                  <div className="inline-flex items-center px-6 py-3 bg-gray-100 rounded-full">
                     <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     <span className="text-sm text-gray-600">{t('report.all_reports_support_date_range')}</span>
                  </div>
               </div>
            </div>
         )}

         {/* Back Button */}
         {selectedReportType && (
            <div className="mt-8">
               <button
                  onClick={() => setSelectedReportType(null)}
                  className="group flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
               >
                  <div className="w-8 h-8 bg-gray-200 group-hover:bg-gray-300 rounded-full flex items-center justify-center mr-3 transition-colors duration-200">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                     </svg>
                  </div>
                  <span className="font-medium">{t('report.back_to_report_selection')}</span>
               </button>
            </div>
         )}

         {/* Report Content - Only show when report type is selected */}
         {selectedReportType && (
            <>

               {/* Conditional Report Content */}
               {selectedReportType === 'order' && (
                  <OrderReport />
               )}

               {selectedReportType === 'stocklogs' && (
                  <StockLogsReport />
               )}
            </>
         )}

      </div>
   );
}

export default Report;
